import { debounce } from "@mongez/reinforcements";
import { ShopProduct } from "core/catalog";
import { parseError } from "core/shared";
import { useCallback, useMemo, useState } from "react";
import { errorToast } from "~/design-system/components";
import { useFetch } from "~/shared/hooks";

const RECENT_SEARCHES_KEY = "recentSearches";

const recentSearchesMap = new Map<string, ShopProduct[]>();

export function useSearchProducts() {
  const [products, setProducts] = useState<ShopProduct[]>([]);
  const [recentSearches, setRecentSearches] = useState<
    [string, ShopProduct[]][]
  >(Array.from(recentSearchesMap.entries()));
  const [loading, setLoading] = useState(false);

  const { load } = useFetch<{ products: ShopProduct[] }>(
    "/search-auto-complete",
  );

  const clearSearchResults = () => {
    recentSearchesMap.clear();
    setRecentSearches([]);
  };

  const handleSearch = useCallback(async (value: string) => {
    if (!value || value.trim() === "") return;

    if (recentSearchesMap.has(value.toLowerCase())) {
      setProducts(recentSearchesMap.get(value.toLowerCase()) || []);
      return;
    }

    setLoading(true);

    try {
      const data = await load({ q: value });

      setProducts(data.products);
    } catch (error) {
      errorToast(parseError(error));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRecentSearch = useCallback(
    (query: string) => {
      recentSearchesMap.set(query.toLowerCase(), products);
      setRecentSearches(Array.from(recentSearchesMap.entries()));
    },
    [products],
  );

  const debouncedHandleSearch = useMemo(
    () => debounce(handleSearch, 500),
    [handleSearch],
  );

  return {
    products,
    loading,
    search: debouncedHandleSearch,
    handleRecentSearch,
    clearSearchResults,
    recentSearches,
  };
}
